import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Image, Upload, X, Loader2 } from 'lucide-react';
import type { CloudinaryUploadWidgetOptions, CloudinaryInterface } from '@/types/cloudinary';
import { apiClient } from '@/lib/apiClient';

interface BusinessImages {
  logo?: string;
  cover?: string;
  gallery: string[];
}

interface BusinessImageUploadProps {
  businessId: string;
  images: BusinessImages;
  onImagesUpdate: (images: BusinessImages) => void;
}

// Ensure window.cloudinary exists
declare global {
  interface Window {
    cloudinary: CloudinaryInterface;
  }
}

export function BusinessImageUpload({ businessId, images, onImagesUpdate }: BusinessImageUploadProps) {
  const { toast } = useToast();
  const { t } = useTranslation('business', { keyPrefix: 'settings' });
  const { t: tc } = useTranslation('common');
  
  const [isUploading, setIsUploading] = useState<{
    logo?: boolean;
    cover?: boolean;
    gallery?: boolean;
  }>({});

  // Initialize Cloudinary widget
  const initializeWidget = useCallback(async (type: 'logo' | 'cover' | 'gallery') => {
    try {
      // Get upload signature
      const { data } = await apiClient.post('/business/images/upload-signature', {
        folder: `business/${businessId}/${type}`
      });

      const { signature, timestamp, cloudName, apiKey } = data.data;

      // Open Cloudinary widget
      const widgetOptions: CloudinaryUploadWidgetOptions = {
        cloudName,
        apiKey,
        signature, // Changed from uploadSignature to signature
        timestamp, // Added timestamp parameter
        uploadPreset: 'business_images',
        folder: `business/${businessId}/${type}`,
        sources: ['local', 'url', 'camera'],
        multiple: type === 'gallery',
        maxFiles: type === 'gallery' ? 10 : 1,
        styles: {
          palette: {
            window: '#FFFFFF',
            windowBorder: '#90A0B3',
            tabIcon: '#0078FF',
            menuIcons: '#5A616A',
            textDark: '#000000',
            textLight: '#FFFFFF',
            link: '#0078FF',
            action: '#FF620C',
            inactiveTabIcon: '#0E2F5A',
            error: '#F44235',
            inProgress: '#0078FF',
            complete: '#20B832',
            sourceBg: '#E4EBF1'
          }
        }
      };

      const widget = window.cloudinary.createUploadWidget(
        widgetOptions,
        async (error: any, result: any) => {
          if (error) {
            console.error('Upload error:', error);
            toast({
              title: tc('error'),
              description: t('imageUploadError'),
              variant: 'destructive'
            });
            setIsUploading(prev => ({ ...prev, [type]: false }));
            return;
          }

          if (result.event === 'success') {
            const imageUrl = result.info.secure_url;
            
            try {
              // Update business images using the correct endpoint structure
              const endpoint = type === 'gallery' 
                ? `/business/images/gallery`
                : `/business/images/${type}`;
                
              const { data: response } = await apiClient.post(endpoint, { imageUrl });
              onImagesUpdate(response.data);
              
              toast({
                title: t('imageUploadSuccess'),
                description: t('imageUploadSuccessDescription')
              });
            } catch (error) {
              console.error('Error updating business images:', error);
              toast({
                title: tc('error'),
                description: t('imageUpdateError'),
                variant: 'destructive'
              });
            }
          }
          
          if (result.event === 'close') {
            setIsUploading(prev => ({ ...prev, [type]: false }));
          }
        }
      );

      setIsUploading(prev => ({ ...prev, [type]: true }));
      widget.open();
    } catch (error) {
      console.error('Error initializing upload widget:', error);
      toast({
        title: tc('error'),
        description: t('imageWidgetError'),
        variant: 'destructive'
      });
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  }, [businessId, toast, t, tc, onImagesUpdate]);

  const handleRemoveGalleryImage = async (imageUrl: string) => {
    try {
        const { data: response } = await apiClient.delete(`/business/images/gallery`, {
            data: { imageUrl }
          });
      
      onImagesUpdate(response.data);
      
      toast({
        title: t('imageRemoveSuccess'),
        description: t('imageRemoveSuccessDescription')
      });
    } catch (error) {
      console.error('Error removing gallery image:', error);
      toast({
        title: tc('error'),
        description: t('imageRemoveError'),
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Logo Upload - Priority 1 */}
      <Card className="border border-blue-200/60 shadow-md shadow-blue-900/5 bg-white hover:shadow-lg hover:shadow-blue-900/8 transition-shadow duration-200 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-blue-600"></div>
        <CardHeader className="pb-4 border-b border-slate-100/80">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center shadow-sm">
                <Image className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                {t('businessLogo')}
                <div className="text-sm text-blue-600 font-medium flex items-center gap-1 mt-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  {t('priority1Essential')}
                </div>
              </div>
            </CardTitle>
            <div className="flex items-center gap-2">
              {images.logo && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  {t('uploaded')}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            {images.logo ? (
              <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-slate-200/60 shadow-sm">
                <img 
                  src={images.logo} 
                  alt="Business Logo" 
                  className="w-full h-full object-cover"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white"
                  onClick={() => initializeWidget('logo')}
                >
                  <Upload className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-32 h-32 flex flex-col items-center justify-center gap-2 border-dashed"
                onClick={() => initializeWidget('logo')}
                disabled={isUploading.logo}
              >
                {isUploading.logo ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-xs">{t('uploading')}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6" />
                    <span className="text-xs">{t('uploadLogo')}</span>
                  </>
                )}
              </Button>
            )}
            <div className="flex-1">
              <h4 className="font-medium text-slate-900 mb-2">{t('logoGuidelines')}</h4>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>{t('logoGuidelineSize')}</li>
                <li>{t('logoGuidelineFormat')}</li>
                <li>{t('logoGuidelineQuality')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cover Image Upload - Priority 2 */}
      <Card className="border border-purple-200/60 shadow-md shadow-purple-900/5 bg-white hover:shadow-lg hover:shadow-purple-900/8 transition-shadow duration-200 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-purple-600"></div>
        <CardHeader className="pb-4 border-b border-slate-100/80">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-purple-200 border border-purple-200/50 flex items-center justify-center shadow-sm">
                <Image className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                {t('coverImage')}
                <div className="text-sm text-purple-600 font-medium flex items-center gap-1 mt-1">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  {t('priority2Recommended')}
                </div>
              </div>
            </CardTitle>
            <div className="flex items-center gap-2">
              {images.cover && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  {t('uploaded')}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {images.cover ? (
              <div className="relative w-full h-48 rounded-lg overflow-hidden border border-slate-200/60 shadow-sm">
                <img 
                  src={images.cover} 
                  alt="Cover Image" 
                  className="w-full h-full object-cover"
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute top-2 right-2 bg-white/90 hover:bg-white"
                  onClick={() => initializeWidget('cover')}
                >
                  <Upload className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full h-48 flex flex-col items-center justify-center gap-2 border-dashed"
                onClick={() => initializeWidget('cover')}
                disabled={isUploading.cover}
              >
                {isUploading.cover ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-sm">{t('uploading')}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6" />
                    <span className="text-sm">{t('uploadCover')}</span>
                  </>
                )}
              </Button>
            )}
            <div>
              <h4 className="font-medium text-slate-900 mb-2">{t('coverGuidelines')}</h4>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                <li>{t('coverGuidelineSize')}</li>
                <li>{t('coverGuidelineRatio')}</li>
                <li>{t('coverGuidelineQuality')}</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gallery Upload - Priority 3 */}
      <Card className="border border-emerald-200/60 shadow-md shadow-emerald-900/5 bg-white hover:shadow-lg hover:shadow-emerald-900/8 transition-shadow duration-200 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600"></div>
        <CardHeader className="pb-4 border-b border-slate-100/80">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 border border-emerald-200/50 flex items-center justify-center shadow-sm">
                <Image className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                {t('imageGallery')}
                <div className="text-sm text-emerald-600 font-medium flex items-center gap-1 mt-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  {t('priority3Optional')}
                </div>
              </div>
            </CardTitle>
            <div className="flex items-center gap-2">
              {images.gallery && images.gallery.length > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  {images.gallery.length} image{images.gallery.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {images.gallery?.map((imageUrl, index) => (
                <div 
                  key={index}
                  className="relative aspect-square rounded-lg overflow-hidden border border-slate-200/60 shadow-sm group"
                >
                  <img 
                    src={imageUrl} 
                    alt={`Gallery Image ${index + 1}`} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      variant="outline"
                      size="icon"
                      className="bg-white/90 hover:bg-white"
                      onClick={() => handleRemoveGalleryImage(imageUrl)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                className="aspect-square flex flex-col items-center justify-center gap-2 border-dashed"
                onClick={() => initializeWidget('gallery')}
                disabled={isUploading.gallery}
              >
                {isUploading.gallery ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-xs">{t('uploading')}</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6" />
                    <span className="text-xs">{t('addToGallery')}</span>
                  </>
                )}
              </Button>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-medium text-emerald-900 mb-2 flex items-center gap-2">
                <span>💡</span>
                How to Use Your Image Library
              </h4>
              <div className="text-sm text-emerald-800 space-y-2">
                <div className="font-medium">These images will be available for:</div>
                <ul className="space-y-1 ml-4 list-disc list-inside">
                  <li>Activity images (assign in Activities section)</li>
                  <li>Custom adventure backgrounds</li>
                  <li>Fallback images when specific images aren't set</li>
                </ul>
                <div className="mt-3 pt-2 border-t border-emerald-200">
                  <div className="font-medium mb-1">Guidelines:</div>
                  <ul className="space-y-1 text-xs text-emerald-700 list-disc list-inside">
                    <li>{t('galleryGuidelineSize')}</li>
                    <li>{t('galleryGuidelineLimit')}</li>
                    <li>{t('galleryGuidelineQuality')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 